import { useEffect, useState, useCallback } from "react";
import {
  getAllScoreSubmissions,
  approveScoreSubmission,
  managerApproveScoreSubmission,
  scoreFinishedSubmission,
  getScoreReminderRecipients,
  addScoreReminderRecipient,
  deleteScoreReminderRecipient,
  runScoreReminder,
} from "../../actions/studensts";
import { excelScoreSubmissions } from "../exportToExcel";
import Loading from "../../components/Loading";
import UNAUTHORIZED from "../UNAUTHORIZED";

// Step 1: these usernames can do the first-level review/approve.
const APPROVERS = ["asmaa"];
// Step 2: only this role can do the final manager approval.
const MANAGER_ROLE = "super-admin";
// View-only: can see the list and statuses, no approve buttons. Enforced
// server-side too (DenyUsers on the manager-approve route), so this holds
// even for an account whose role would otherwise allow approving.
const VIEWERS = ["abdelrhman", "admin"];
// Can open the Reminder Emails panel without being able to approve.
const REMINDER_MANAGERS = ["admin"];

// Mirrors SCORE_REMINDER_AFTER_HOURS on the server: a submission still
// pending this long after submitted_at is what the reminder job chases.
const REMINDER_AFTER_HOURS = 48;

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const fmtDateTime = (val) => {
  if (!val) return "";
  const d = new Date(String(val).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// Hours a row has been waiting, or null if submitted_at is unusable.
const hoursWaiting = (val) => {
  if (!val) return null;
  const d = new Date(String(val).replace(" ", "T"));
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 36e5;
};

export default function SendScoreSubmissions() {
  const profile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const username = profile?.username;
  const role = profile?.role;
  const isViewer = VIEWERS.includes(username);
  const isApprover = APPROVERS.includes(username) && !isViewer;
  const canSendScoreFinished = username === "abdelrhman";
  // A view-only username never gets approve controls, whatever its role —
  // mirrors the DenyUsers guard on the server route.
  const isManager = role === MANAGER_ROLE && !isViewer;
  const canManageReminders = isManager || REMINDER_MANAGERS.includes(username);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approvingId, setApprovingId] = useState(null);
  const [managerApprovingId, setManagerApprovingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [reminderError, setReminderError] = useState("");
  const [reminderNotice, setReminderNotice] = useState("");
  const [sendingNow, setSendingNow] = useState(false);
  const [scoreFinishingId, setScoreFinishingId] = useState(null);

  const load = useCallback(async () => {
    const res = await getAllScoreSubmissions();
    if (res?.status === 200) {
      setRows(res.data.data);
    } else {
      setError("Failed to load submissions. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isApprover || isManager || isViewer) load();
  }, [load, isApprover, isManager, isViewer]);

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    const res = await getScoreReminderRecipients();
    if (res?.status === 200) {
      setRecipients(res.data.data);
    } else {
      setReminderError("Failed to load the reminder list. Please try again.");
    }
    setRecipientsLoading(false);
  }, []);

  const openReminders = () => {
    setReminderError("");
    setReminderNotice("");
    setShowRemindersModal(true);
    loadRecipients();
  };

  const handleAddRecipient = async (e) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;
    setSavingEmail(true);
    setReminderError("");
    setReminderNotice("");
    const res = await addScoreReminderRecipient(email);
    setSavingEmail(false);
    if (res?.status === 201) {
      setNewEmail("");
      loadRecipients();
    } else {
      setReminderError(res?.data?.ERROR || "Could not add that email. Please try again.");
    }
  };

  const handleRemoveRecipient = async (id) => {
    setRemovingId(id);
    setReminderError("");
    setReminderNotice("");
    const res = await deleteScoreReminderRecipient(id);
    setRemovingId(null);
    if (res?.status === 200) {
      setRecipients((prev) => prev.filter((r) => r.id !== id));
    } else {
      setReminderError(res?.data?.ERROR || "Could not remove that email. Please try again.");
    }
  };

  // Runs the same job the hourly cron runs. It still honours the 48h cutoff
  // and the once-a-day repeat window, so nothing is sent if there is nothing
  // currently due.
  const handleSendNow = async () => {
    setSendingNow(true);
    setReminderError("");
    setReminderNotice("");
    const res = await runScoreReminder();
    setSendingNow(false);
    if (res?.status === 200) {
      const { submissions = 0, sent = 0 } = res.data || {};
      setReminderNotice(
        submissions > 0
          ? `Reminder sent to ${sent} recipient(s) covering ${submissions} submission(s).`
          : "Nothing is due right now — no submission is past 48 hours and unreminded today."
      );
    } else {
      setReminderError(res?.data?.ERROR || "Sending the reminder failed. Please try again.");
    }
  };

  const handleSendScoreFinished = async (id) => {
    setScoreFinishingId(id);
    setError("");
    const res = await scoreFinishedSubmission(id);
    setScoreFinishingId(null);
    if (res?.status === 200) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, score_finished: 1, score_finished_by: username }
            : r
        )
      );
    } else {
      setError(res?.data?.ERROR || "Send Score Finished failed. Please try again.");
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError("");
    const res = await approveScoreSubmission(id);
    setApprovingId(null);
    if (res?.status === 200) {
      // Status stays "pending" until the manager approval step below runs.
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, asmaa_approved: 1, asmaa_approved_by: username }
            : r
        )
      );
    } else {
      setError(res?.data?.ERROR || "Approve failed. Please try again.");
    }
  };

  const handleManagerApprove = async (id) => {
    setManagerApprovingId(id);
    setError("");
    const res = await managerApproveScoreSubmission(id);
    setManagerApprovingId(null);
    if (res?.status === 200) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: "approved", manager_approved_by: username }
            : r
        )
      );
    } else {
      setError(res?.data?.ERROR || "Manager approval failed. Please try again.");
    }
  };

  if (!isApprover && !isManager && !isViewer) {
    return <UNAUTHORIZED />;
  }

  const pendingCount = rows.filter((r) => r.status === "pending").length;
  const overdueCount = rows.filter(
    (r) => r.status === "pending" && (hoursWaiting(r.submitted_at) ?? 0) >= REMINDER_AFTER_HOURS
  ).length;

  // Filters by the submitted_at date (not time), so "To" is inclusive of
  // that whole day. Either bound can be left blank for an open-ended range;
  // leaving both blank exports every row currently listed.
  const handleExport = async () => {
    setExporting(true);
    const from = exportDateFrom;
    const to = exportDateTo;
    const filtered =
      from || to
        ? rows.filter((r) => {
            const day = String(r.submitted_at || "").slice(0, 10);
            return (!from || day >= from) && (!to || day <= to);
          })
        : rows;
    await excelScoreSubmissions(filtered, from || null, to || null);
    setExporting(false);
    setShowExportModal(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl text-white">Send Score Submissions</h1>
        <div className="flex items-center gap-3">
          <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
            {pendingCount} pending
          </span>
          {overdueCount > 0 && (
            <span className="bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
              {overdueCount} over {REMINDER_AFTER_HOURS}h
            </span>
          )}
          {canManageReminders && (
            <button
              onClick={openReminders}
              className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded"
            >
              Reminder Emails
            </button>
          )}
          <button
            onClick={() => setShowExportModal(true)}
            disabled={loading || rows.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-50"
          >
            Export to Excel
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
        )}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-gray-500 text-center py-10">No submissions yet.</div>
        ) : (
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2">Full Name</th>
                  <th className="text-left px-4 py-2">National ID</th>
                  <th className="text-left px-4 py-2">ACT ID</th>
                  <th className="text-left px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Submitted At</th>
                  <th className="text-left px-4 py-2">Submitted By</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Asmaa Review</th>
                  <th className="text-center px-4 py-2">Action</th>
                  {canSendScoreFinished && (
                    <th className="text-center px-4 py-2">Send Score</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t ${
                      r.score_finished ? "bg-green-100" : "odd:bg-gray-50"
                    }`}
                  >
                    <td className="px-4 py-2">{r.full_name || "—"}</td>
                    <td className="px-4 py-2">{r.national_id}</td>
                    <td className="px-4 py-2">{r.act_id || "—"}</td>
                    <td className="px-4 py-2">{r.amount} {r.currency || "EGP"}</td>
                    <td className="px-4 py-2">
                      {fmtDateTime(r.submitted_at)}
                      {r.status === "pending" &&
                        (hoursWaiting(r.submitted_at) ?? 0) >= REMINDER_AFTER_HOURS && (
                          <span
                            className="ml-2 inline-block bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full"
                            title={
                              r.last_reminder_at
                                ? `Last reminder sent ${fmtDateTime(r.last_reminder_at)}`
                                : "Reminder not sent yet"
                            }
                          >
                            {Math.floor(hoursWaiting(r.submitted_at))}h waiting
                          </span>
                        )}
                    </td>
                    <td className="px-4 py-2">{r.submitted_by || "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_STYLES[r.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {r.asmaa_approved ? (
                        <span className="text-green-700 text-xs font-medium">
                          ✓ {r.asmaa_approved_by || "asmaa"}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {r.status === "approved" ? (
                        <span className="text-green-700 text-xs font-medium">✓ Approved</span>
                      ) : !r.asmaa_approved ? (
                        isApprover ? (
                          <button
                            onClick={() => handleApprove(r.id)}
                            disabled={approvingId === r.id}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                          >
                            {approvingId === r.id ? "Approving..." : "Approve"}
                          </button>
                        ) : (
                          <span className="text-gray-500 text-xs">Awaiting review</span>
                        )
                      ) : isManager ? (
                        <button
                          onClick={() => handleManagerApprove(r.id)}
                          disabled={managerApprovingId === r.id}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          {managerApprovingId === r.id ? "Approving..." : "Manager Approve"}
                        </button>
                      ) : (
                        <span className="text-gray-500 text-xs">Awaiting manager approval</span>
                      )}
                    </td>
                    {canSendScoreFinished && (
                      <td className="px-4 py-2 text-center">
                        {r.score_finished ? (
                          <span className="text-green-700 text-xs font-medium">
                            ✓ Send score finished
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSendScoreFinished(r.id)}
                            disabled={scoreFinishingId === r.id}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                          >
                            {scoreFinishingId === r.id ? "Sending..." : "Send Score Finished"}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reminder Recipients Modal — manager role + named reminder managers */}
      {showRemindersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60"
            onClick={() => !savingEmail && !sendingNow && setShowRemindersModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-7 w-[30rem] z-10">
            <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-3">
              Reminder Emails
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              When a submission is still not approved {REMINDER_AFTER_HOURS} hours
              after it was submitted, a reminder listing every overdue submission
              is mailed to these addresses from noreply@americanacademyeg.com. It
              repeats once a day until the submission is approved.
            </p>

            {reminderError && (
              <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
                {reminderError}
              </div>
            )}
            {reminderNotice && (
              <div className="mb-3 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
                {reminderNotice}
              </div>
            )}

            <form onSubmit={handleAddRecipient} className="flex gap-2 mb-4">
              <input
                type="email"
                placeholder="name@americanacademyeg.com"
                className="flex-1 border border-gray-300 rounded-lg p-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={savingEmail}
              />
              <button
                type="submit"
                disabled={savingEmail || !newEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                           disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
              >
                {savingEmail ? "Adding..." : "Add"}
              </button>
            </form>

            <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-5">
              {recipientsLoading ? (
                <div className="py-6 text-center text-gray-500 text-sm">Loading...</div>
              ) : recipients.length === 0 ? (
                <div className="py-6 text-center text-gray-500 text-sm">
                  No addresses yet — no reminders will be sent until you add one.
                </div>
              ) : (
                recipients.map((r) => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="text-sm text-gray-800">{r.email}</div>
                      {r.created_by && (
                        <div className="text-xs text-gray-400">added by {r.created_by}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveRecipient(r.id)}
                      disabled={removingId === r.id}
                      className="text-red-600 hover:text-red-800 text-xs font-semibold disabled:opacity-50"
                    >
                      {removingId === r.id ? "Removing..." : "Remove"}
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-between items-center gap-3">
              <button
                onClick={handleSendNow}
                disabled={sendingNow || recipients.length === 0}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700
                           disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                title="Run the reminder now instead of waiting for the hourly check"
              >
                {sendingNow ? "Sending..." : "Send Now"}
              </button>
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg
                           hover:bg-gray-200 text-sm font-medium"
                onClick={() => setShowRemindersModal(false)}
                disabled={savingEmail || sendingNow}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Date Filter Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60"
            onClick={() => !exporting && setShowExportModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-7 w-96 z-10">
            <h2 className="text-lg font-bold text-gray-800 mb-5 border-b pb-3">
              Export to Excel
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Filter by submitted date, or leave both blank to export every
              submission currently listed.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                From (Submitted Date)
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg p-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={exportDateFrom}
                onChange={(e) => setExportDateFrom(e.target.value)}
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                To (Submitted Date)
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg p-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={exportDateTo}
                onChange={(e) => setExportDateTo(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg
                           hover:bg-gray-200 text-sm font-medium"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                disabled={exporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                           disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                onClick={handleExport}
              >
                {exporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
